import React from "react";
import PanelShell from "../../shared/PanelShell";
import { SkeletonText } from "../../shared/Skeleton";
import { fmtNumber, labelFor } from "../../shared/format";
import { useTheme } from "../../../contexts/ThemeContext";
import { BAR_RAMP_LIGHT, BAR_RAMP_DARK, barFill } from "./barRamp";
import CrossChannelNote from "./crossChannelNote";
import { ROW_HOVER } from "./rowStyles";

// WHAT PEOPLE WANTED — demand by stay type, ranked. Its only two call sites are
// voice_demand and chat_demand; barriers moved off this panel in C1, which is
// what left it demand-only.
//
// MARKUP BARS, NOT A RECHARTS CHART. §2.2 makes a demand segment a drill source,
// and a Recharts <Bar> cell is an SVG rect: it takes a click but is not in the
// tab order, carries no accessible name, and cannot be given the 44px touch box
// the accessibility floor requires. That is the same argument ChatQuestionsPanel
// already records for the topic bars, and it decides this panel the same way —
// a row of <button>s is keyboard-reachable, screen-readable and hit-testable for
// free. The mark is a proportional fill on the SEQUENTIAL ramp (rows arrive
// sorted, so index 0 is the strongest step), which reads in greyscale and in
// print the way the chart's hue-free ramp did.
//
// It also drops a fixed 240px container in favour of one sized to its rows,
// which is what §3.4 asks for. Nothing here is a downgrade to text: the bar is
// still a bar.
//
// demand is by_stay_type / by_urgency; historically also barriers, which is why
// `toBarRows` still normalises the array form.
// `summary.demand.by_stay_type` and `by_urgency` come back as OBJECT MAPS
// ({nightly: 44, monthly: 18, …}), while `top_barriers` is an array of
// {barrier, count}. An array-only guard rendered the two maps as "No data for
// this period" on every property. Normalise both to {name, value}.
export const toBarRows = (rows) => {
  if (Array.isArray(rows)) {
    return rows.map((r) => ({
      name: r.label || r.stay_type || r.urgency || r.barrier || r.name,
      value: r.count ?? r.value ?? r.occurrences,
    }));
  }
  if (rows && typeof rows === "object") {
    return Object.entries(rows).map(([name, value]) => ({ name, value }));
  }
  return [];
};

const Row = ({ row, i, fill, max, onSelect }) => {
  const label = labelFor(row.name);
  // A proportion of the LARGEST row in the set — a drawing instruction, never a
  // number the panel shows. Floored at 4% so the smallest segment is still a
  // mark rather than a hairline.
  const width = max > 0 ? Math.max(4, Math.round((Number(row.value) / max) * 100)) : 0;
  const body = (
    <>
      <span className="flex items-baseline justify-between gap-2">
        <span className="gi-body text-gray-900 dark:text-gray-100 truncate">{label}</span>
        <span className="gi-label text-gray-500 dark:text-gray-400 tabular-nums shrink-0 normal-case tracking-normal">
          {fmtNumber(row.value)}
        </span>
      </span>
      <span className="mt-1 block h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
        <span className="block h-full rounded-full" style={{ width: `${width}%`, backgroundColor: fill }} />
      </span>
    </>
  );
  return (
    <li data-segment={row.name}>
      {onSelect ? (
        <button
          type="button"
          onClick={() => onSelect({ key: row.name, label, count: row.value })}
          className={`w-full text-left min-h-[44px] py-1.5 rounded-md ${ROW_HOVER}`}
          title={`Show conversations from ${label}`}
        >
          {body}
        </button>
      ) : (
        <div className="py-1.5">{body}</div>
      )}
    </li>
  );
};

const BarsPanel = ({ panelKey, label, say, rows, loading, topN, crossChannel = false, onSelect, ordered = false }) => {
  const { isDarkMode } = useTheme();
  const ramp = isDarkMode ? BAR_RAMP_DARK : BAR_RAMP_LIGHT;
  const normalised = toBarRows(rows).filter((r) => r.value != null);
  // Ranked consumers (the default) sort by count, largest first, and cap the
  // list. The demand band (§E.9) renders the backend's payload order instead —
  // nightly → … → workamper is a fixed sequence the reader scans top to bottom,
  // not a leaderboard — and shows every band (E.9 allows up to seven), so it
  // neither re-sorts nor caps. The backend already returns the rows in band
  // order and an object map preserves its insertion order through toBarRows, so
  // honouring that order is the whole fix.
  const data = ordered
    ? normalised
    : [...normalised].sort((a, b) => Number(b.value) - Number(a.value)).slice(0, topN || 8);
  const max = data.reduce((m, r) => Math.max(m, Number(r.value)), 0);
  return (
    <PanelShell panelKey={panelKey} label={label} say={say} hasData={data.length > 0} loading={loading} className="flex flex-col">
      {loading && !rows ? <SkeletonText lines={5} /> : (
        <ul data-testid="demand-rows">
          {data.map((row, i) => (
            <Row key={row.name} row={row} i={i} fill={barFill(ramp, i)} max={max} onSelect={onSelect} />
          ))}
        </ul>
      )}
      <CrossChannelNote when={crossChannel} />
    </PanelShell>
  );
};

export default BarsPanel;
